import {
  AppstoreAddOutlined,
  DeleteOutlined,
  FunctionOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Space, Tooltip, Typography } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import * as React from 'react';
import { useFieldArray } from 'react-hook-form';
import styled from 'styled-components';
import { ExpressionType } from '../../../IExpression';
import { ComputeExpressionMinimalInput } from '../../../compute/input/ComputeExpressionMinimalInput';
import { IReference } from '../../../references/IReference';
import {
  ConditionGroupOperator,
  IConditionItem,
} from '../../condition-group/IConditionGroupExpression';
import { ConditionGroupInput } from '../../condition-group/input/ConditionGroupInput';
import { ConditionInput } from './ConditionInput';
const { Text } = Typography;

export interface IConditionsListInputProps {
  name: string;
  references: IReference[];
  allowGroup?: boolean;

  // per grafica
  nested?: boolean;
  size?: SizeType;
}

const $ConditionsListWrapper = styled.div`
  padding-top: 8px;
  // fix per rimuovere padding top inserito in form-handler
  &&& {
    .ant-form-item {
      padding-top: 0;
    }
  }
  // fix regole funzioni inserite come ultimo elemento
  // differenziate da quelle inserite come prime
  .rule-expression-editor {
    margin-right: -1px;
    .rte-input-editor {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
  // nel caso di inserimento anche in un form è l'elemento finale
  .ant-form-item-control-input {
    .rule-expression-editor {
      .rte-input-editor {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  }
`;

const $ConditionWrapper = styled.div`
  padding-bottom: 8px;
`;

export function ConditionsListInput(props: IConditionsListInputProps) {
  const { name, references, allowGroup, size } = props;

  const { fields, append, remove } = useFieldArray<
    Record<string, IConditionItem[]>,
    string
  >({ name });

  return (
    <$ConditionsListWrapper>
      {fields.map((field, index) => (
        <$ConditionWrapper key={field.id}>
          <Space align="start">
            {field.type === ExpressionType.Condition && (
              <ConditionInput
                size={size ?? 'middle'}
                references={references}
                name={`${name}[${index}]`}
              />
            )}
            {field.type === ExpressionType.Function && (
              <ComputeExpressionMinimalInput
                key="function"
                size={size ?? 'middle'}
                name={`${name}[${index}].value`}
                references={references}
              />
            )}
            {field.type === ExpressionType.ConditionGroup && (
              <ConditionGroupInput
                references={references}
                name={`${name}[${index}]`}
                nested
                size={size ?? 'middle'}
              />
            )}
            {fields.length > 0 && (
              <Button
                onClick={() => remove(index)}
                danger
                type={!props.nested ? 'default' : 'primary'}
                icon={<DeleteOutlined />}
                size={size ?? 'middle'}
              />
            )}
          </Space>
        </$ConditionWrapper>
      ))}
      <Space>
        <Button
          type={!props.nested ? 'default' : 'primary'}
          icon={<PlusOutlined aria-hidden="true" />}
          size={size ?? 'middle'}
          onClick={() =>
            append({
              type: ExpressionType.Condition,
              reference: null as unknown as IReference,
              operator: null as unknown as string,
              arguments: [] as any[],
            })
          }
        />
        <Tooltip title="Aggiungi una Funzione">
          <Button
            type={!props.nested ? 'default' : 'primary'}
            icon={<FunctionOutlined />}
            size={size ?? 'middle'}
            onClick={() =>
              append({
                type: ExpressionType.Function,
                value: {} as any,
              })
            }
          />
        </Tooltip>
        {allowGroup && (
          <Tooltip title="Aggiungi un sotto-gruppo di condizioni">
            <Button
              type={!props.nested ? 'default' : 'primary'}
              icon={<AppstoreAddOutlined />}
              size={size ?? 'middle'}
              onClick={() =>
                append({
                  type: ExpressionType.ConditionGroup,
                  operator: ConditionGroupOperator.And,
                  conditions: [],
                })
              }
            />
          </Tooltip>
        )}
        {fields.length < 1 && (
          <Text type="secondary">
            Aggiungi una condizione per impostare una regola
          </Text>
        )}
      </Space>
    </$ConditionsListWrapper>
  );
}
